import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

const STORAGE_KEY = 'kp:recently-viewed';
const MAX_STORED = 12;

/**
 * Reads the stored product IDs, most recent first.
 * @returns {string[]}
 */
function readViewed() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * Moves a product to the front of the stored list.
 * @param {string} productId
 */
function recordViewed(productId) {
  const ids = [productId, ...readViewed().filter((id) => id !== productId)].slice(0, MAX_STORED);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage full or blocked (private mode): the section simply stays hidden.
  }
}

/**
 * Recently viewed products (sections/kp-recently-viewed.liquid).
 *
 * Records the current product, then re-renders this section through the search
 * route with the other viewed IDs as the query. Stays hidden when there is no
 * history. Uses its own storage key so it can hold more than the four IDs
 * Horizon keeps for predictive search.
 */
class KpRecentlyViewed extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { productId, sectionId } = this.dataset;
    if (!productId || !sectionId || Shopify.designMode) return;

    const max = Number(this.dataset.maxProducts) || 4;
    const others = readViewed()
      .filter((id) => id !== productId)
      .slice(0, max);

    recordViewed(productId);
    if (others.length === 0) return;

    this.#load(sectionId, others);
  }

  /**
   * @param {string} sectionId
   * @param {string[]} ids - Product IDs, most recent first
   */
  async #load(sectionId, ids) {
    const url = new URL(this.dataset.searchUrl || '/search', location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      await morphSection(sectionId, await response.text(), { mode: 'hydration', injectStylesheet: true });
      if (this.querySelector('.resource-list__item')) this.hidden = false;
    } catch (error) {
      console.error('Recently viewed error:', error);
    }
  }
}

if (!customElements.get('kp-recently-viewed')) {
  customElements.define('kp-recently-viewed', KpRecentlyViewed);
}
